import { useCallback, useEffect, useState } from 'react'
import { CheckCircle2, AlertTriangle, Info, XCircle, X } from 'lucide-react'
import './AlertCenter.css'

type ToastType = 'info' | 'success' | 'warn' | 'error'

interface Toast {
  id: number
  message: string
  type: ToastType
  duration: number
}

let nextId = 0

export function AlertCenter() {
  const [toasts, setToasts] = useState<Toast[]>([])

  const remove = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  useEffect(() => {
    const handler = (e: Event) => {
      const { message, type = 'info', duration = 4000 } = (e as CustomEvent).detail
      const id = ++nextId
      setToasts(prev => {
        const next = [...prev, { id, message, type, duration }]
        return next.length > 5 ? next.slice(-5) : next
      })
      if (duration > 0) {
        setTimeout(() => remove(id), duration)
      }
    }
    window.addEventListener('runai:toast', handler as EventListener)
    return () => window.removeEventListener('runai:toast', handler as EventListener)
  }, [remove])

  if (toasts.length === 0) return null

  const iconFor = (type: ToastType) => {
    switch (type) {
      case 'success': return <CheckCircle2 size={14} />
      case 'warn': return <AlertTriangle size={14} />
      case 'error': return <XCircle size={14} />
      default: return <Info size={14} />
    }
  }

  return (
    <div className="runai-alert-center">
      {toasts.map(t => (
        <div key={t.id} className={`runai-alert-toast runai-alert-${t.type}`}>
          <span className="runai-alert-toast__icon">{iconFor(t.type)}</span>
          <span className="runai-alert-toast__msg">{t.message}</span>
          <button className="runai-alert-toast__close" onClick={() => remove(t.id)} aria-label="Close">
            <X size={12} />
          </button>
        </div>
      ))}
    </div>
  )
}
